import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowUpRight, Receipt, TrendingUp } from "lucide-react";

export default function MerchantDashboard() {
  const [wallet, setWallet] = useState(null);
  const [links, setLinks] = useState([]);
  const [txns, setTxns] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [w, l, t] = await Promise.all([
          api.get("/wallet"),
          api.get("/payment-links"),
          api.get("/transactions"),
        ]);
        setWallet(w.data);
        setLinks(l.data);
        setTxns(t.data.slice(0, 6));
      } catch {}
    })();
  }, []);

  const totalCollected = links.reduce((s, l) => s + (l.total_collected || 0), 0);
  const totalPaid = links.reduce((s, l) => s + (l.times_paid || 0), 0);
  const balances = wallet?.balances || {};
  const primary = balances.usd ?? 0;
  const otherCurrencies = Object.entries(balances).filter(([c, v]) => c !== "usd" && v > 0);

  return (
    <main data-testid="merchant-dashboard" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-gold">Merchant Console</div>
          <h1 className="font-display text-5xl tracking-tighter mt-3">Good evening.</h1>
        </div>
        <Link to="/merchant/links/new" data-testid="new-link-cta" className="btn-gold inline-flex items-center gap-2">
          <Plus size={16} /> New payment link
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard testid="stat-balance" label="Wallet · USD" value={formatMoney(primary)} accent />
        <StatCard testid="stat-collected" label="Total collected" value={formatMoney(totalCollected)} icon={<TrendingUp size={16} />} />
        <StatCard testid="stat-paid" label="Successful payments" value={totalPaid.toString()} icon={<Receipt size={16} />} />
      </div>
      {otherCurrencies.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-4 text-sm">
          <span className="text-muted-fg uppercase tracking-widest text-xs">Other balances</span>
          {otherCurrencies.map(([c, v]) => (
            <span key={c} data-testid={`other-balance-${c}`} className="text-gold font-mono">
              {formatMoney(v, c)}
            </span>
          ))}
        </div>
      )}

      <section className="mt-16">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-3xl tracking-tight">Recent payment links</h2>
          <Link to="/merchant/links" data-testid="view-all-links" className="text-sm text-gold hover:text-white transition-colors flex items-center gap-1">
            View all <ArrowUpRight size={14} />
          </Link>
        </div>
        {links.length === 0 ? (
          <Empty title="No payment links yet" cta={{ to: "/merchant/links/new", label: "Create your first link" }} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {links.slice(0, 6).map((l) => (
              <LinkCard key={l.link_id} link={l} />
            ))}
          </div>
        )}
      </section>

      <section className="mt-16">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-3xl tracking-tight">Recent transactions</h2>
          <Link to="/transactions" data-testid="view-ledger" className="text-sm text-gold hover:text-white transition-colors">View ledger →</Link>
        </div>
        <TxList items={txns} />
      </section>
    </main>
  );
}

function StatCard({ label, value, icon, accent, testid }) {
  return (
    <div data-testid={testid} className={`lux-card p-8`}>
      <div className="flex items-center justify-between text-xs uppercase tracking-[0.25em] text-muted-fg">
        <span>{label}</span>
        {icon && <span className="text-gold">{icon}</span>}
      </div>
      <div className={`font-display text-5xl mt-6 tracking-tighter ${accent ? "gold-text" : ""}`}>{value}</div>
    </div>
  );
}

function LinkCard({ link }) {
  const url = `${window.location.origin}/pay/${link.link_id}`;
  return (
    <div data-testid={`payment-link-card-${link.link_id}`} className="lux-card p-6 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-fg">{link.currency?.toUpperCase()}</div>
          <div className="font-display text-3xl mt-2 gold-text">{formatMoney(link.amount, link.currency)}</div>
        </div>
        <div className="text-xs text-muted-fg">{link.times_paid || 0} paid</div>
      </div>
      <div className="mt-1">
        <div className="text-sm font-medium">{link.title}</div>
        {link.description && <div className="text-xs text-secondary-fg mt-1 line-clamp-2">{link.description}</div>}
      </div>
      <button
        data-testid={`copy-link-${link.link_id}`}
        onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }}
        className="text-xs font-mono text-secondary-fg hover:text-gold transition-colors truncate text-left mt-2"
        title={url}
      >
        {url}
      </button>
    </div>
  );
}

function TxList({ items }) {
  if (!items?.length) return <Empty title="No transactions yet" />;
  return (
    <div className="border hairline">
      {items.map((t) => (
        <div key={t.tx_id} data-testid={`tx-row-${t.tx_id}`} className="grid grid-cols-12 gap-4 px-6 py-5 border-b hairline last:border-b-0 hover:bg-surface transition-colors">
          <div className="col-span-6">
            <div className="text-sm">{t.description || t.type}</div>
            <div className="text-xs text-muted-fg mt-1">{new Date(t.created_at).toLocaleString()}</div>
          </div>
          <div className="col-span-3 text-xs uppercase tracking-widest text-muted-fg self-center">{t.type?.replace(/_/g, " ")}</div>
          <div className={`col-span-3 text-right font-display text-xl ${t.direction === "in" ? "text-gold" : "text-secondary-fg"}`}>
            {t.direction === "in" ? "+" : "−"} {formatMoney(t.amount, t.currency)}
          </div>
        </div>
      ))}
    </div>
  );
}

function Empty({ title, cta }) {
  return (
    <div className="border hairline p-12 text-center">
      <div className="font-display text-2xl text-secondary-fg">{title}</div>
      {cta && (
        <Link to={cta.to} className="btn-gold inline-block mt-6">{cta.label}</Link>
      )}
    </div>
  );
}
